import { useLazyQuery } from '@apollo/client';
import { isThisYear, isToday, isTomorrow } from 'date-fns';
import equal from 'deep-equal';
import { KeyboardEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import TertiaryButton from '@components/buttons/TertiaryButton';
import DateStamp from '@components/dates/DateStamp';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import styles from '@components/expert/ExpertTasksPage.module.css';
import TaskFilters, { Filters } from '@components/forms/TaskFilters';
import TaskForm from '@components/forms/TaskForm';
import HandleQuery from '@components/HandleQuery';
import Modal from '@components/Modal';
import ExpertTablesPage from '@components/pageTypes/ExpertTablesPage';
import Table, { TableHeader } from '@components/Table';
import TaskTableRow from '@components/TaskTableRow';
import HugeText from '@components/text/HugeText';
import useSubscribeToExpertTasks from '@hooks/subscriptions/useSubscribeToExpertTasks';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import useOpener from '@hooks/useOpener';
import {
  ExpertGlobalRoleEnum,
  ExpertTasksSortByEnum,
  GetExpertTasksDocument,
  GetExpertTasksQuery,
  GetExpertTasksQueryVariables,
  SortDirectionEnum,
  TaskFragment as Task,
  TaskAssigneeEnum,
  TaskDestroyDocument,
  TaskDestroyMutation,
  TaskDestroyMutationVariables,
  TaskStatusEnum,
  TaskToggleCompletedDocument,
  TaskToggleCompletedMutation,
  TaskToggleCompletedMutationVariables
} from '@typing/Generated';
import { groupByTimestampDay } from '@utils/modelUtils';

const ExpertTasksPage = () => {
  const intl = useIntl();
  const taskOpener = useOpener();
  const deleteTaskOpener = useOpener();

  const myExpert = useMyExpert();

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<Filters>({
    assignee: TaskAssigneeEnum.all,
    clientId: null,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? null : myExpert?.id,
    status: TaskStatusEnum.incomplete
  });

  const [destroyTask, client] = useMutationWithValidation<TaskDestroyMutation, TaskDestroyMutationVariables>(
    TaskDestroyDocument,
    'task',
    {
      update: (_store, { data }) => {
        if (data?.taskDestroy) {
          client.resetStore();
        }
      }
    }
  );

  const [toggleTaskCompleted, apolloClient] = useMutationWithValidation<
    TaskToggleCompletedMutation,
    TaskToggleCompletedMutationVariables
  >(TaskToggleCompletedDocument, 'task', {
    update: () => {
      // This kicks out all of the expertTasks queries so we will re-make them. If you're flipping between filters
      // then the queries related to them will be cached, but if completed changed then we need to redo those queries.
      apolloClient.cache.evict({ fieldName: 'expertTasks', id: 'ROOT_QUERY' });
    }
  });

  const handleTaskDestroy = useCallback(() => {
    if (editingTask) {
      destroyTask({
        id: editingTask.id
      });
    }
  }, [editingTask, destroyTask]);

  const handleEditTask = useCallback(
    (task: Task | null) => {
      setEditingTask(task);
      taskOpener.open();
    },
    [taskOpener]
  );

  const handleTaskCreate = useCallback(() => {
    setEditingTask(null);
    taskOpener.open();
  }, [taskOpener]);

  const handleTaskToggleCompleted = useCallback(
    (task: Task | null) => {
      if (task) {
        toggleTaskCompleted({
          completed: !task.completed,
          id: task.id
        });
      }
    },
    [toggleTaskCompleted]
  );

  const handleTaskCreateKeyUp: KeyboardEventHandler = useCallback(
    event => {
      if (event.code === 'Enter') {
        setEditingTask(null);
        taskOpener.open();
      }
    },
    [taskOpener]
  );

  const [sortBy, setSortBy] = useState<ExpertTasksSortByEnum>(ExpertTasksSortByEnum.due_time);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.ascending);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        if (newFilters.status !== filters.status && newFilters.status === TaskStatusEnum.completed) {
          setSortDirection(SortDirectionEnum.descending);
        }
        if (newFilters.status !== filters.status && newFilters.status === TaskStatusEnum.incomplete) {
          setSortDirection(SortDirectionEnum.ascending);
        }
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getTasks, getTasksQuery] = useLazyQuery<GetExpertTasksQuery, GetExpertTasksQueryVariables>(
    GetExpertTasksDocument
  );

  useEffect(() => {
    if (myExpert) {
      getTasks({
        variables: {
          assignee: filters.assignee,
          clientId: filters.clientId,
          expertId: filters.expertId,
          sortBy,
          sortDirection,
          status: filters.status
        }
      });
    }
  }, [getTasks, filters.assignee, filters.clientId, filters.status, myExpert, filters.expertId, sortBy, sortDirection]);

  useSubscribeToExpertTasks(myExpert?.id ?? '', () => {
    getTasksQuery.refetch();
  });

  const allTasks = useNodesFromConnection(getTasksQuery.data?.expertTasks);
  const tasksGroupedByDay = useMemo(
    () =>
      groupByTimestampDay<Task>(
        allTasks,
        task => task.dueTime,
        intl,
        !(sortBy === ExpertTasksSortByEnum.due_time && sortDirection === SortDirectionEnum.descending)
      ),
    [allTasks, intl, sortBy, sortDirection]
  );

  const dateForGroup = useCallback(
    (date: Date) => {
      if (isToday(date)) {
        return intl.formatMessage(
          { id: 'pages.expert.tasks.days.today' },
          { date: <FormattedDate day="numeric" month="long" value={date} /> }
        );
      }
      if (isTomorrow(date)) {
        return intl.formatMessage(
          { id: 'pages.expert.tasks.days.tomorrow' },
          { date: <FormattedDate day="numeric" month="long" value={date} /> }
        );
      }
      return <DateStamp showYear={!isThisYear(date)} value={date} />;
    },
    [intl]
  );

  const tableHeaders: TableHeader<ExpertTasksSortByEnum>[] = [
    { messageKey: 'pages.expert.tasks.tableHeadings.taskTitle' },
    { messageKey: 'pages.expert.tasks.tableHeadings.yourRole' },
    { messageKey: 'pages.expert.tasks.tableHeadings.assignee' },
    { messageKey: 'pages.expert.tasks.tableHeadings.client' },
    { messageKey: 'pages.expert.tasks.tableHeadings.lovedOne' },
    { messageKey: 'pages.expert.tasks.tableHeadings.clientVisible' },
    { columnSortBy: ExpertTasksSortByEnum.due_time }
  ];

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.task.list">
      <HandleQuery query={getTasksQuery}>
        {_data => (
          <>
            <div className={styles.goBack}>
              <TertiaryButton
                icon="chevronLeft"
                routerLink="/expert/dashboard"
                textKey="pages.expert.consults.dashboard"
              />
            </div>
            <div className={styles.bannerGroup}>
              <div className={styles.title}>
                <HugeText>
                  <FormattedMessage id="pages.expert.tasks.tasks" />
                </HugeText>
              </div>
              <TertiaryButton
                icon="add"
                onClick={handleTaskCreate}
                onKeyUp={handleTaskCreateKeyUp}
                textKey="pages.expert.dashboard.addTask"
              />
            </div>
            <div className={styles.tableContainer}>
              <TaskFilters filters={filters} onChange={handleFilterChange} />
            </div>
            <Table<ExpertTasksSortByEnum>
              fetchMore={getTasksQuery.fetchMore}
              headerKey="pages.expert.tasks.tableHeadings"
              headers={tableHeaders}
              pageInfo={getTasksQuery.data?.expertTasks.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {tasksGroupedByDay.map(group =>
                group.models.map((task, index) => (
                  <TaskTableRow
                    getStringForDate={dateForGroup}
                    handleTaskEdit={handleEditTask}
                    handleTaskToggleCompleted={handleTaskToggleCompleted}
                    hiddenDate={index !== 0}
                    key={task.id}
                    task={task}
                  />
                ))
              )}
            </Table>
          </>
        )}
      </HandleQuery>

      <>
        <Modal opener={taskOpener} title={intl.formatMessage({ id: 'forms.task.addTask' })}>
          {({ modalDisplayed }) => (
            <TaskForm
              autofocus={modalDisplayed}
              onCancel={taskOpener.close}
              onSuccess={taskOpener.close}
              refetch={getTasksQuery.refetch}
              task={editingTask}
            />
          )}
        </Modal>

        <DeleteConfirmationAlert
          messageKey="pages.expert.tasks.areYouSure"
          messageValue={
            editingTask
              ? editingTask.title ?? intl.formatMessage({ id: `models.tasks.kind.${editingTask?.kind ?? ''}` })
              : ''
          }
          messageValueKey="title"
          onDelete={() => handleTaskDestroy()}
          opener={deleteTaskOpener}
        />
      </>
    </ExpertTablesPage>
  );
};

export default ExpertTasksPage;
